import React from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { Wallet } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTenantConfig } from '../contexts/TenantConfigContext';
import TabContainer from '../components/TabContainer';
import GeneralExpensesTab from '../components/payments/GeneralExpensesTab';
import DiscountsTab from '../components/payments/DiscountsTab';

const PageContainer = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  direction: rtl;
  background: white;
  min-height: 100vh;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`;

/**
 * הגדרת טאב במודול ניהול התשלומים.
 * allowedRoles - רמות ההרשאה שרשאיות לראות את הטאב (1=מנהל מערכת, 4=מזכירה).
 *
 * הרחבה עתידית (פרומפטים נפרדים): להוסיף כאן ערך נוסף, למשל:
 *   { key: 'discounts', title: 'הנחות', allowedRoles: [1], render: <DiscountsTab /> }
 *   { key: 'tithes',    title: 'מעשרות', allowedRoles: [1], render: <TithesTab /> }
 * ללא צורך בשינוי המעטפת או המיכל.
 */
interface PaymentsTabConfig {
  key: string;
  title: string;
  allowedRoles: number[];
  render: React.ReactNode;
}

const TAB_CONFIG: PaymentsTabConfig[] = [
  {
    key: 'general-expenses',
    title: 'הוצאות כלליות',
    allowedRoles: [1, 4], // מנהל מערכת + מזכירה
    render: <GeneralExpensesTab />,
  },
  {
    key: 'discounts',
    title: 'הנחות',
    allowedRoles: [1], // מנהל מערכת בלבד
    render: <DiscountsTab />,
  },
];

const PaymentsManagementPage: React.FC = () => {
  const { user } = useAuth();
  const { config, isLoading } = useTenantConfig();
  const permissionLevel = user?.permissionLevel ?? 0;

  // גידור tenant: המודול מופעל רק עבור חברה שה-flag דלוק אצלה (כיום "מגיע לך").
  // ה-UI מסתיר את העמוד; אכיפת האמת היא בשרת (403). כאן מונעים גישה ישירה ל-URL.
  if (isLoading) {
    return null;
  }
  if (!config?.paymentsManagementEnabled) {
    return <Navigate to="/unauthorized" replace />;
  }

  const visibleTabs = TAB_CONFIG
    .filter(tab => tab.allowedRoles.includes(permissionLevel))
    .map(tab => ({ key: tab.key, title: tab.title, content: tab.render }));

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>
          <Wallet size={28} />
          ניהול תשלומים
        </PageTitle>
      </PageHeader>

      {visibleTabs.length > 0 ? (
        <TabContainer tabs={visibleTabs} />
      ) : (
        <p>אין לך הרשאה לצפות בתוכן זה.</p>
      )}
    </PageContainer>
  );
};

export default PaymentsManagementPage;
